import { Replay } from '@mui/icons-material';
import { CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { PTZContext } from 'components/ExtendedVideoPlayer/context';
import useScript from 'kineticUtils/hooks/useScript';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import defaultVideoPlayerStyles from '../../styles';
import { GenetecVideoPlayerProps, GenetecWebPlayer } from './types';

// genetec web player module
declare global {
  interface Window {
    gwp: {
      buildPlayer(element: HTMLDivElement): GenetecWebPlayer;
    };
  }
}

type Props = Omit<GenetecVideoPlayerProps, 'streamType'>;

const MEDIA_PATH = 'media';
const VERSION = 'v2';
const TOKEN_PATH = `${MEDIA_PATH}/${VERSION}/token`;
const PLAYER_PATH = `${MEDIA_PATH}/${VERSION}/files/gwp.js`;

const GenetecVideoPlayer: React.FunctionComponent<Props> = (props) => {
  const { className, setShowPTZControls, retry = true, setStreamLoaded, settings, url } = props;
  const username = settings?.USERNAME;
  const password = settings?.PASSWORD;
  const certificate = settings?.API_KEY;
  const cameraId = settings?.EXTERNAL_ID;

  const credentials = useMemo(() => {
    if (!username || !certificate || !password) {
      return null;
    } else {
      return username + ';' + certificate + ':' + password;
    }
  }, [certificate, password, username]);
  const videoRef = useRef<HTMLDivElement | null>(null);
  const classes = defaultVideoPlayerStyles();
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(true);
  const [isStreamAvailable, setIsStreamAvailable] = useState(true);
  const [reload, setReload] = useState(false);
  const playerRef = useRef<GenetecWebPlayer>();
  // load the genetec web player script from the genetec server
  const scriptStatus = useScript(getGwpUrl(url));

  const { panSpeed, tiltSpeed, zoomSpeed } = useContext(PTZContext);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (!isStreamAvailable && !reload) {
      timeout = setTimeout(() => {
        setReload(true);
      }, 1500);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isStreamAvailable, reload]);

  // use the web players ptz control to move the camera
  useEffect(() => {
    try {
      if (playerRef.current) {
        if (panSpeed === 0 && tiltSpeed === 0) {
          playerRef.current?.ptzControl?.stopPanTilt();
        } else {
          playerRef.current?.ptzControl?.startPanTilt(panSpeed, tiltSpeed);
        }
      }
    } catch (error) {
      console.error('Error setting pan and tilt speed:', error);
    }
  }, [panSpeed, tiltSpeed]);
  useEffect(() => {
    try {
      if (playerRef.current) {
        if (zoomSpeed === 0) {
          playerRef.current?.ptzControl?.stopZoom();
        } else {
          playerRef.current?.ptzControl?.startZoom(zoomSpeed);
        }
      }
    } catch (error) {
      console.error('Error setting zoom speed:', error);
    }
  }, [zoomSpeed]);

  useEffect(() => {
    let toggleStreamLoaded: ReturnType<typeof setTimeout>;
    if (!loading) {
      toggleStreamLoaded = setTimeout(() => setStreamLoaded?.(!loading), 700);
    } else {
      setStreamLoaded?.(!loading);
    }
    return () => toggleStreamLoaded && clearTimeout(toggleStreamLoaded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    let waitForGwpTimer: NodeJS.Timeout;

    const _initPlayer = () => {
      if (!url || scriptStatus === 'error' || !credentials) {
        setIsStreamAvailable(false);
        setLoading(false);
        return;
      }
      setLoading(scriptStatus !== 'loaded');

      if (scriptStatus === 'loaded' && !window.gwp) {
        // sometimes the script is loaded but the window object is not yet available
        waitForGwpTimer = setTimeout(() => _initPlayer(), 10);
        return;
      }

      setIsStreamAvailable(true);
      if (videoRef.current && window.gwp) {
        if (playerRef.current) {
          try {
            // dispose of the old player
            playerRef.current.dispose();
          } catch (error) {
            console.error('Error disposing player:', error);
          }
        }
        try {
          // the genetec server script loads the gwp module into the window object
          playerRef.current = window.gwp.buildPlayer(videoRef.current);
          const endpoint = getMediaUrl(url);
          if (!cameraId) {
            throw new Error('Camera ID is required');
          }
          playerRef.current
            .start(cameraId, endpoint, getTokenRetriever(url, credentials))
            .then(() => {
              if (!playerRef.current?.m_isDisposed) {
                playerRef.current?.setPtzMode(true);
                playerRef.current?.playLive();
              }
            });
        } catch (error) {
          console.error('Error creating player:', error);
        }
      }
    };
    if (reload) {
      setReload(false);
    }
    _initPlayer();
    return () => {
      clearTimeout(waitForGwpTimer);
      if (playerRef.current) {
        try {
          playerRef.current.dispose();
        } catch (error) {
          console.error('Error disposing player:', error);
        }
      }
    };
  }, [url, reload, scriptStatus, cameraId, credentials]);

  useEffect(() => {
    setShowPTZControls?.(isStreamAvailable);
  }, [isStreamAvailable, setShowPTZControls]);

  useEffect(() => {
    if (!username || !password || !certificate || !cameraId) {
      setIsStreamAvailable(false);
    }
  }, [cameraId, certificate, password, username]);

  return (
    <div style={{ position: 'relative' }} className={classes.videoWrapper}>
      <div className={className ? className : classes.video} ref={videoRef}></div>
      {loading ? (
        <div className={className ? className : classes.loading}>
          <CircularProgress />
        </div>
      ) : !isStreamAvailable ? (
        <div className={className ? className : classes.unavailableWrapper}>
          <div>
            <div className={className ? className : classes.unavailable}>
              <Typography variant='body2'>{t('streamUnavailable')}</Typography>
            </div>
            {retry && (
              <div className={className ? className : classes.retryWrapper}>
                <Tooltip title={t('retry')}>
                  <IconButton
                    size='medium'
                    className={className ? className : classes.retryButton}
                    onClick={() => setReload(true)}
                  >
                    <Replay fontSize='medium' />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const getGwpUrl = (url: string) => {
  const urlWithoutTrailingSlash = removeTrailingSlash(url);
  return `${urlWithoutTrailingSlash}/${PLAYER_PATH}`;
};

const getMediaUrl = (url: string) => {
  const urlWithoutTrailingSlash = removeTrailingSlash(url);
  return `${urlWithoutTrailingSlash}/${MEDIA_PATH}`;
};

const removeTrailingSlash = (url: string) => {
  return url.replace(/\/$/, '');
};

const getTokenUrl = (url: string, cameraId: string) => {
  const urlWithoutTrailingSlash = removeTrailingSlash(url);
  return `${urlWithoutTrailingSlash}/${TOKEN_PATH}/${cameraId}`;
};

function getTokenRetriever(url: string, credentials: string) {
  return async (cameraId: string) => {
    try {
      const tokenResponse = await fetch(getTokenUrl(url, cameraId), {
        credentials: 'include',
        headers: {
          Authorization: `Basic ${btoa(credentials)}`,
        },
      });
      if (!tokenResponse.ok) {
        throw tokenResponse.status;
      }
      return await tokenResponse.text();
    } catch (error) {
      console.error('Error retrieving token', error);
      throw error;
    }
  };
}

export default React.memo(GenetecVideoPlayer);
